// Labels, help text and badge colours for every enum the billing API
// returns.
//
// **Every list of options is built from the generated enum const**, never
// from a hand-written array. When the backend gains a value, a select
// here grows an unlabelled-but-present option rather than quietly
// missing one — and `label()` falls back to a title-cased version of the
// wire value, so nothing renders as a blank.
//
// The same reason drives `label(value: string)` rather than
// `label(value: LateFeeType)`: a ledger line's `entry_type` is typed as
// a plain `string` in the generated client, so the lookups have to
// accept one.
//
// This file is copy only. Anything that computes belongs in
// `src/utils/billingHelpers.ts`.

import {
  CaseStatus,
  ClosingReason,
  CollectionsEventType,
  DayCount,
  DueRule,
  FeeBasis,
  FineStatus,
  Frequency,
  InterestAccrual,
  InterestMethod,
  LateFeeType,
  LedgerEntryType,
  MemberRole,
  NoticeAction,
  NoticeChannel,
  NoticeTrigger,
  PaymentMethod,
  PaymentStatus,
  ProrationMethod,
  ReviewCondition,
  RoundingMode,
  RunStatus,
  ScheduleStatus,
  TenureStatus,
  TurnoverBalancePolicy,
} from '../clients/rust-api-client';

/** The colours `common/Catalyst/badge.tsx` accepts. */
export type BadgeColor =
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | 'zinc';

interface EnumMeta {
  label: string;
  /** One line of field help, shown under the control. */
  help?: string;
  color?: BadgeColor;
  /**
   * Ledger entry types only: this type increases what the homeowner
   * owes. Mirrors `LedgerEntryType::is_charge` on the backend.
   *
   * It is here rather than in a list of its own so that a new entry
   * type arrives next to the flag it needs. **A charge type added
   * without `charge: true` will not be offerable in the payment
   * application order** — it still bills correctly, it just cannot be
   * given a position, and falls to oldest-first with everything else.
   */
  charge?: boolean;
  /**
   * Ledger entry types only: a person may post this by hand on
   * `POST /charges` or `POST /credits`. Mirrors
   * `LedgerEntryType::is_manually_chargeable` and
   * `is_manually_creditable`.
   *
   * Most types are absent from both on purpose — `assessment` is
   * written by a billing run, `payment` by a real-world event with a
   * check number that can bounce, `reversal` by reversing something.
   * Offering them in a picker would be offering a 400.
   */
  manual?: 'charge' | 'credit';
}

export interface EnumOption {
  value: string;
  label: string;
  help?: string;
  color?: BadgeColor;
  /** Representable in the API but refused at write — render disabled. */
  disabled?: boolean;
}

export interface EnumDescriptor {
  /** Every value the generated client knows about, in wire form. */
  values: string[];
  label: (value?: string | null) => string;
  help: (value?: string | null) => string | undefined;
  color: (value?: string | null) => BadgeColor;
  options: () => EnumOption[];
  /** Values flagged `charge` — see {@link EnumMeta.charge}. */
  charges: () => string[];
  /** Values a person may post by hand — see {@link EnumMeta.manual}. */
  manual: (kind: 'charge' | 'credit') => EnumOption[];
}

/** `late_notice` → `Late notice`, for a value we have no copy for. */
const titleCase = (value: string): string => {
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const defineEnum = <T extends string>(
  source: Record<string, T>,
  meta: Partial<Record<T, EnumMeta>>,
  disabled: ReadonlyArray<T> = []
): EnumDescriptor => {
  const values = Object.values(source);
  const lookup = (value?: string | null): EnumMeta | undefined =>
    value === null || value === undefined ? undefined : meta[value as T];

  return {
    values,
    label: (value) =>
      value === null || value === undefined || value === ''
        ? '-'
        : (lookup(value)?.label ?? titleCase(value)),
    help: (value) => lookup(value)?.help,
    color: (value) => lookup(value)?.color ?? 'zinc',
    options: () =>
      values.map((value) => ({
        value,
        label: meta[value]?.label ?? titleCase(value),
        help: meta[value]?.help,
        color: meta[value]?.color,
        disabled: disabled.indexOf(value) !== -1,
      })),
    charges: () => values.filter((value) => meta[value]?.charge),
    manual: (kind) =>
      values
        .filter((value) => meta[value]?.manual === kind)
        .map((value) => ({
          value,
          label: meta[value]?.label ?? titleCase(value),
          help: meta[value]?.help,
          color: meta[value]?.color,
        })),
  };
};

// ---------------------------------------------------------------------
// Billing policy
// ---------------------------------------------------------------------

export const lateFeeType = defineEnum(LateFeeType, {
  none: {
    label: 'No late fee',
    help: 'Nothing is ever charged for paying late.',
  },
  flat: {
    label: 'Flat amount',
    help: 'A fixed dollar amount, whatever the charge was.',
  },
  percent: {
    label: 'Percentage',
    help: 'A percentage of the basis below — so the amount is a percentage, not dollars.',
  },
});

export const feeBasis = defineEnum(FeeBasis, {
  overdue_charge: {
    label: 'The overdue charge',
    help: 'Assessed against each charge that has gone past its grace period.',
  },
  total_balance: {
    label: "The account's whole balance",
    help: 'Assessed against everything outstanding, not just the late charge.',
  },
});

export const dayCount = defineEnum(DayCount, {
  actual365: {
    label: 'Actual / 365',
    help: 'Actual days over a 365-day year.',
  },
  actual360: {
    label: 'Actual / 360',
    help: 'Actual days over a 360-day year — about 1.4% more interest per day than actual/365.',
  },
  thirty360: {
    label: '30 / 360',
    help: 'Every month counted as 30 days, over a 360-day year (US NASD).',
  },
});

export const interestMethod = defineEnum(InterestMethod, {
  simple: {
    label: 'Simple',
    help: 'Interest accrues on the charge only.',
  },
  compound: {
    label: 'Compound',
    help: 'Accrued interest itself accrues interest.',
  },
});

export const interestAccrual = defineEnum(InterestAccrual, {
  daily: { label: 'Daily' },
  monthly: { label: 'Monthly' },
});

export const roundingMode = defineEnum(RoundingMode, {
  half_up: {
    label: 'Half up',
    help: 'The common default: .005 rounds away from zero.',
  },
  half_even: { label: "Half even (banker's)" },
  down: {
    label: 'Down',
    help: 'Always toward zero — rounds in the homeowner’s favour.',
  },
  up: { label: 'Up', help: 'Always away from zero.' },
});

export const prorationMethod = defineEnum(ProrationMethod, {
  none: {
    label: 'No proration',
    help: 'A part period is charged in full.',
  },
  by_day: {
    label: 'By day',
    help: 'Amount × days owned ÷ days in the period.',
  },
  by_month: {
    label: 'By whole month',
    help: 'The natural rule for quarterly and annual dues, where a daily split reads as false precision.',
  },
});

export const turnoverBalancePolicy = defineEnum(TurnoverBalancePolicy, {
  settle_at_closing: {
    label: 'Settle at closing',
    help: "An unpaid balance stays on the seller's retired account: out of the buyer's Amount Due, still collectible from them or through the lien.",
  },
  carry_forward: {
    label: 'Carry forward to the buyer',
    help: "Open charges move to the new owner's account, so the buyer's Amount Due includes them.",
  },
});

// ---------------------------------------------------------------------
// Dues schedules
// ---------------------------------------------------------------------

export const frequency = defineEnum(Frequency, {
  monthly: { label: 'Monthly' },
  quarterly: { label: 'Quarterly' },
  semiannual: { label: 'Twice a year' },
  annual: { label: 'Annually' },
  one_time: { label: 'One time' },
});

/**
 * `nth_weekday` is representable and **refused at create** — the enum
 * exists from day one so adding it later is a match arm rather than a
 * migration. Rendering it disabled is better than letting somebody pick
 * it and collect a 400.
 */
export const dueRule = defineEnum(
  DueRule,
  {
    day_of_month: {
      label: 'A day of the month',
      help: 'Pick the day below. A 31st is clamped in shorter months.',
    },
    last_day_of_period: {
      label: 'The last day of the period',
      help: 'Always the real last day, so February needs no special case.',
    },
    nth_weekday: {
      label: 'The nth weekday (not implemented yet)',
      help: 'Modelled but not yet supported — creating a schedule with this is refused.',
    },
  },
  ['nth_weekday']
);

export const scheduleStatus = defineEnum(ScheduleStatus, {
  draft: { label: 'Draft', color: 'zinc' },
  active: { label: 'Active', color: 'lime' },
  ended: { label: 'Ended', color: 'zinc' },
});

// ---------------------------------------------------------------------
// Billing runs
// ---------------------------------------------------------------------

/**
 * A `pending` run is not an error — it is the reviewable object the
 * backend was built to produce, and the variance guard holding one is
 * the system working. Amber says "needs a person", not "broken".
 */
export const runStatus = defineEnum(RunStatus, {
  pending: {
    label: 'Awaiting review',
    help: 'Generated but not posted. Nothing has reached a homeowner’s ledger yet.',
    color: 'amber',
  },
  posted: {
    label: 'Posted',
    help: 'On the ledger. Undoing it means reversing every entry.',
    color: 'lime',
  },
  reversed: {
    label: 'Reversed',
    help: 'Every entry has an opposing entry against it.',
    color: 'zinc',
  },
});

// ---------------------------------------------------------------------
// The ledger
// ---------------------------------------------------------------------

/**
 * Charges are positive and credits are negative, and the colours follow
 * that rather than the type name: `transfer_in` and `transfer_out` are
 * the pair a `carry_forward` turnover writes, and they are neither a
 * charge somebody incurred nor a payment somebody made.
 */
export const ledgerEntryType = defineEnum(LedgerEntryType, {
  assessment: { label: 'Dues', color: 'zinc', charge: true },
  special_assessment: {
    label: 'Special assessment',
    help: 'A one-off levy on top of the regular dues.',
    color: 'zinc',
    charge: true,
    manual: 'charge',
  },
  late_fee: {
    label: 'Late fee',
    help: 'Normally written by the late-fee job; post one by hand only to correct it.',
    color: 'amber',
    charge: true,
    manual: 'charge',
  },
  interest: {
    label: 'Interest',
    help: 'Normally written by the interest job.',
    color: 'amber',
    charge: true,
    manual: 'charge',
  },
  violation_fine: { label: 'Violation fine', color: 'amber', charge: true },
  nsf_fee: {
    label: 'NSF fee',
    help: 'Normally written when a payment is returned.',
    color: 'amber',
    charge: true,
    manual: 'charge',
  },
  other_charge: {
    label: 'Other charge',
    help: 'Anything the other types do not cover — a transfer fee, a key deposit.',
    color: 'zinc',
    charge: true,
    manual: 'charge',
  },
  charge_adjustment: {
    label: 'Charge adjustment',
    help: 'Increases what is owed, to correct a charge that was too low.',
    color: 'zinc',
    charge: true,
    manual: 'charge',
  },
  refund: {
    label: 'Refund',
    help: 'Money returned to the homeowner, which increases what they owe again.',
    color: 'zinc',
    charge: true,
    manual: 'charge',
  },
  payment: { label: 'Payment', color: 'lime' },
  waiver: {
    label: 'Waiver',
    help: 'The association chooses not to collect a charge it could.',
    color: 'sky',
    manual: 'credit',
  },
  write_off: {
    label: 'Write-off',
    help: 'The debt is judged uncollectable and comes off the books.',
    color: 'sky',
    manual: 'credit',
  },
  credit_adjustment: {
    label: 'Credit',
    help: 'Reduces what is owed, to correct a charge that was too high.',
    color: 'sky',
    manual: 'credit',
  },
  reversal: { label: 'Reversal', color: 'purple' },
  transfer_out: {
    label: 'Transferred out',
    help: 'Moved off this account at a turnover.',
    color: 'zinc',
  },
  transfer_in: {
    label: 'Transferred in',
    help: "Carried forward from the previous owner's account at a turnover.",
    color: 'zinc',
    charge: true,
  },
  amenity_fee: {
    label: 'Amenity fee',
    help: 'Posted by an approved reservation.',
    color: 'zinc',
    charge: true,
  },
  amenity_deposit: {
    label: 'Amenity deposit',
    help: 'Held against damage until the booking is settled — a liability, not income.',
    color: 'zinc',
    charge: true,
  },
  amenity_fee_return: {
    label: 'Amenity fee returned',
    help: 'A paid reservation fee given back. Paid out by a refund unless the association allows credit balances.',
    color: 'sky',
  },
  amenity_deposit_return: {
    label: 'Amenity deposit returned',
    help: 'A paid deposit given back; clears it from deposits held. Paid out by a refund unless the association allows credit balances.',
    color: 'sky',
  },
});

// ---------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------

export const paymentMethod = defineEnum(PaymentMethod, {
  check: { label: 'Check' },
  ach: { label: 'ACH' },
  e_check: { label: 'eCheck' },
  card: { label: 'Card' },
  cash: { label: 'Cash' },
  manual: { label: 'Manual' },
  other: { label: 'Other' },
});

/**
 * **`overpaid` is money held, not a negative balance.** The remainder
 * sits unapplied on the payment and is refundable; the ledger balance
 * does not go below zero unless the HOA allows credit balances. Sky,
 * not red — it is a decision waiting, not a debt.
 */
export const paymentStatus = defineEnum(PaymentStatus, {
  unmatched: {
    label: 'In suspense',
    help: 'Arrived on an account number we could not resolve. Match it to a home.',
    color: 'amber',
  },
  applied: { label: 'Applied', color: 'lime' },
  overpaid: {
    label: 'Overpaid — held',
    help: 'More than the balance. The remainder is held on this payment and can be refunded.',
    color: 'sky',
  },
  returned: {
    label: 'Returned (NSF)',
    help: 'Bounced. Its applications are reversed and the charges it paid are open again.',
    color: 'red',
  },
  voided: { label: 'Voided', color: 'zinc' },
  refunded: { label: 'Refunded', color: 'zinc' },
});

// ---------------------------------------------------------------------
// Ownership tenures
// ---------------------------------------------------------------------

export const tenureStatus = defineEnum(TenureStatus, {
  pending: { label: 'Pending', color: 'amber' },
  active: { label: 'Active', color: 'lime' },
  retired: { label: 'Retired', color: 'zinc' },
});

export const closingReason = defineEnum(ClosingReason, {
  sale: { label: 'Sale' },
  foreclosure: { label: 'Foreclosure' },
  transfer: { label: 'Transfer' },
  correction: {
    label: 'Correction',
    help: 'The tenure should not have existed — not a change of owner.',
  },
});

export const memberRole = defineEnum(MemberRole, {
  owner: { label: 'Owner' },
  co_owner: { label: 'Co-owner' },
  tenant: { label: 'Tenant' },
});

// ---------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------

/**
 * The aging job opens a case and takes it as far as `late_notice`.
 * Everything below that line carries legal consequences and is a
 * decision a person makes — which is why they are Owner/Admin only.
 */
export const caseStatus = defineEnum(CaseStatus, {
  reminder: {
    label: 'Reminder',
    help: 'Where a case opens, whether the aging job or a person opened it.',
    color: 'yellow',
  },
  late_notice: {
    label: 'Late notice',
    help: 'As far as the aging job will take a case on its own.',
    color: 'amber',
  },
  demand: { label: 'Demand', color: 'orange' },
  attorney: { label: 'With attorney', color: 'red' },
  lien: { label: 'Lien filed', color: 'red' },
  foreclosure: { label: 'Foreclosure', color: 'red' },
  resolved: {
    label: 'Resolved',
    help: 'Closes by itself when the account is paid up.',
    color: 'lime',
  },
});

export const collectionsEventType = defineEnum(CollectionsEventType, {
  opened: { label: 'Opened' },
  notice_sent: { label: 'Notice sent' },
  notice_flagged: {
    label: 'Notice flagged for review',
    help: 'A rule set to flag rather than send — it is waiting for a person to release it.',
  },
  advanced: { label: 'Advanced' },
  payment_received: { label: 'Payment received' },
  note: { label: 'Note' },
  resolved: { label: 'Resolved' },
});

// ---------------------------------------------------------------------
// Notice rules
// ---------------------------------------------------------------------

export const noticeTrigger = defineEnum(NoticeTrigger, {
  days_before_due: { label: 'Days before due' },
  days_after_due: { label: 'Days after due' },
  balance_threshold: {
    label: 'Balance over a threshold',
    help: 'Needs a minimum balance — without one it fires on every home that owes a cent.',
  },
});

export const noticeAction = defineEnum(NoticeAction, {
  send: {
    label: 'Send automatically',
    help: 'Dispatched by the notice job without anyone looking.',
    color: 'lime',
  },
  flag: {
    label: 'Flag for review',
    help: 'Raised for a person to release. The right setting for a demand letter.',
    color: 'amber',
  },
});

export const noticeChannel = defineEnum(NoticeChannel, {
  email: { label: 'Email' },
  portal: { label: 'Member portal' },
  mail: { label: 'Post' },
});

// ---------------------------------------------------------------------
// The review queue
// ---------------------------------------------------------------------

/**
 * Each of these is a predicate over state that exists anyway, and each
 * resolves through a real action with a ledger consequence — there is
 * nothing to dismiss, and no acknowledgement to record.
 */
export const reviewCondition = defineEnum(ReviewCondition, {
  overpayment: {
    label: 'Overpayment held',
    help: 'Refund it, apply it forward, or write it off.',
    color: 'sky',
  },
  unmatched_payment: {
    label: 'Payment in suspense',
    help: 'Decide which home it belongs to.',
    color: 'amber',
  },
  payment_on_retired_tenure: {
    label: 'Payment on a retired account',
    help: 'Apply it to the old owner or the new one.',
    color: 'amber',
  },
  returned_payment: {
    label: 'Returned payment',
    help: 'Fee, notice, collections.',
    color: 'red',
  },
  balance_drift: {
    label: 'Balance cache drift',
    help: 'A bug to investigate. Reporting it never repairs it — the evidence is the point.',
    color: 'red',
  },
  run_pending: {
    label: 'Run generated, never posted',
    help: 'Post it or discard it. A run only appears here after a day.',
    color: 'amber',
  },
  delinquent_past_threshold: {
    label: 'Delinquent past the threshold',
    help: 'An open case has reached this association’s hold threshold.',
    color: 'orange',
  },
  retired_tenure_with_balance: {
    label: 'Retired account still owing',
    help: 'A settle-at-closing remainder — pursue the prior owner or the lien.',
    color: 'amber',
  },
});

// ---------------------------------------------------------------------
// Violation fines
// ---------------------------------------------------------------------
//
// A fine is an ordinary ledger charge, so its status is derived from
// what has been paid against that entry rather than stored on the
// violation. These moved here from `utils/billingHelpers.ts` when this
// file landed.

export const fineStatus = defineEnum(FineStatus, {
  unpaid: { label: 'Unpaid', color: 'red' },
  partially_paid: { label: 'Part paid', color: 'amber' },
  paid: { label: 'Paid', color: 'lime' },
  reversed: {
    label: 'Withdrawn',
    help: 'The charge was reversed — the homeowner no longer owes it.',
    color: 'zinc',
  },
});
